import { Box } from '@factorialco/deprecated-design-system'
import { OneEmptyState, Spinner } from '@factorialco/f0-react/dist/experimental'
import { Split } from '@factorialco/f0-react/icons/app'
import { ProcurementTypeItemFragment } from 'generated/resources/graphql'
import {
  CreateWorkflowsWorkflowDocument,
  FindWorkflowDocument,
  WorkflowGraphFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useBannerContext } from 'modules/layout/hooks/useBannerContext'
import { basicTaskApproveNodeToSchemaMapper } from 'modules/procurement/components/procurementTypes/Workflows/sidePanels/schemas/nodeToSchemaMappers/basicTask'
import { manualApproveNodeToSchemaMapper } from 'modules/procurement/components/procurementTypes/Workflows/sidePanels/schemas/nodeToSchemaMappers/manualApprove'
import { EmptyHandlerForm } from 'modules/workflows/components/Sidepanel/handlerForms/EmptyHandlerForm'
import { approvalFormSchema } from 'modules/workflows/components/Sidepanel/schemas/approvalFormSchema'
import { WorkflowContainer } from 'modules/workflows/components/WorkflowContainer'

import RoleSelector from './VariableSelectors/RoleSelector'
import TeamSelector from './VariableSelectors/TeamSelector'
import { BasicTaskHandlerForm } from './sidePanels/BasicTaskHandlerForm'
import { RequestReviewHandlerForm } from './sidePanels/ManualApproveHandlerForm'
import { basicTaskHandlerSchema } from './sidePanels/schemas/basicTaskHandler'
import {
  type ManualApproveHandlerSchema,
  manualApproveHandlerSchema,
} from './sidePanels/schemas/manualApproveHandler'

interface props {
  requestType?: ProcurementTypeItemFragment
  workflow: WorkflowGraphFragment | null
  onWorkflowCreated: (workflow: WorkflowGraphFragment | null) => void
  onSaveRef?: React.MutableRefObject<(() => Promise<void>) | null>
}

const TARGET_TYPE = 'Procurement::Type'
const USE_CASE = 'approvals_purchase_request_approval_flow'

const WorkflowsTab = ({ requestType, workflow, onWorkflowCreated, onSaveRef }: props) => {
  const { company } = useCurrentSession()
  const { hasBanner } = useBannerContext()

  // Query workflow manifest
  const { data: workflowsData, loading: workflowFetching } = useQuery(FindWorkflowDocument, {
    variables: {
      companyId: company?.id || 0,
      useCase: USE_CASE,
      targetType: TARGET_TYPE,
      targetIds: [requestType?.id || 0],
    },
    skip: !company?.id || !requestType?.id,
  })

  // Create workflow mutation
  const [createWorkflow, { loading: isCreating }] = useMutation(CreateWorkflowsWorkflowDocument, {
    extractorFn: (x) => x.workflowsMutations.createWorkflowsWorkflow,
  })

  const workflowManifest = workflowsData?.workflows?.workflowManifestsConnection?.nodes[0]

  const handleCreateWorkflow = async () => {
    if (!company?.id || !requestType?.id) return

    try {
      const result = await createWorkflow({
        variables: {
          companyId: company.id,
          useCase: USE_CASE,
          targetType: TARGET_TYPE,
          targetId: requestType.id,
        },
      })

      const handled = handleResult(result, (errors) => {
        const errorMessages = Object.values(errors).join(', ')
        app.setFlash({
          type: 'wrong',
          message: i18n.t(
            'procurement.purchase_requests.settings.types.detail.tabs.workflows.error.create_failed',
            { errors: errorMessages }
          ),
          time: 3000,
        })
      })

      if (handled.success && handled.data) {
        // Notify parent with the newly created workflow
        const newWorkflow = handled.data.workflow
        if (newWorkflow) {
          onWorkflowCreated(newWorkflow)
        }

        app.setFlash({
          type: 'correct',
          message: i18n.t(
            'procurement.purchase_requests.settings.types.detail.tabs.workflows.success.created'
          ),
          time: 3000,
        })
      }
    } catch (error) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t(
          'procurement.purchase_requests.settings.types.detail.tabs.workflows.error.create_failed',
          { errors: error }
        ),
        time: 3000,
      })
    }
  }

  if (workflowFetching) {
    return (
      <Box height="full" minHeight="s600" justifyContent="center" alignItems="center">
        <Spinner />
      </Box>
    )
  }

  if (workflow && workflowManifest) {
    const heightOffset = hasBanner ? '264px' : '220px'

    return (
      <WorkflowContainer
        heightOffset={heightOffset}
        workflow={workflow}
        workflowManifest={workflowManifest}
        handlerForms={{
          auto_approve: {
            dropDownLabel: i18n.t('workflows.approval.types.auto_approve'),
            component: EmptyHandlerForm,
            initializer: () => ({}),
            schema: approvalFormSchema,
          },
          auto_reject: {
            dropDownLabel: i18n.t('workflows.approval.types.auto_reject'),
            component: EmptyHandlerForm,
            initializer: () => ({}),
            schema: approvalFormSchema,
          },
          request_review: {
            dropDownLabel: i18n.t('workflows.approval.types.request_review'),
            component: RequestReviewHandlerForm,
            beforeSave: (payload, handlerType) => {
              // Type guard for ManualApproveHandlerSchema payload
              const isManualApproveHandlerPayload = (
                value: unknown
              ): value is ManualApproveHandlerSchema['nodeData']['payload'] => {
                if (handlerType == 'request_review') {
                  return true
                }
                return false
              }

              if (!isManualApproveHandlerPayload(payload)) {
                throw new Error('Invalid payload for request_review handler')
              }

              return {
                ...payload,
                employee_segments: payload.employee_segments || [],
              }
            },
            schema: manualApproveHandlerSchema,
            initializer: manualApproveNodeToSchemaMapper,
          },
          create_manual_task: {
            dropDownLabel: i18n.t('workflows.task.types.basic'),
            component: BasicTaskHandlerForm,
            initializer: basicTaskApproveNodeToSchemaMapper,
            schema: basicTaskHandlerSchema,
          },
        }}
        variableSelectors={{
          role_id: {
            component: RoleSelector,
            valueType: 'entity',
            label: 'Role',
          },
          team_id: {
            component: TeamSelector,
            valueType: 'entity',
            label: 'Team',
          },
          amount: {
            component: 'number',
            valueType: 'number',
            label: 'Amount',
          },
        }}
        triggerConfig={{
          title: i18n.t(
            'procurement.purchase_requests.settings.types.detail.tabs.workflows.trigger.title'
          ),
          description: i18n.t(
            'procurement.purchase_requests.settings.types.detail.tabs.workflows.trigger.description'
          ),
        }}
        onSaveRef={onSaveRef}
      />
    )
  }

  // Show Add Button if no workflows exist
  return (
    <Box height="full" minHeight="s600" justifyContent="center" alignItems="center" gap="s24">
      <OneEmptyState
        actions={[
          {
            label: isCreating
              ? i18n.t(
                  'procurement.purchase_requests.settings.types.detail.tabs.workflows.action.creating'
                )
              : i18n.t(
                  'procurement.purchase_requests.settings.types.detail.tabs.workflows.action.add'
                ),
            onClick: handleCreateWorkflow,
            type: 'default',
            variant: 'outline',
            icon: Split,
          },
        ]}
        description={i18n.t(
          'procurement.purchase_requests.settings.types.detail.tabs.workflows.empty.description'
        )}
        emoji="🔀"
        title={i18n.t(
          'procurement.purchase_requests.settings.types.detail.tabs.workflows.empty.title'
        )}
      />
    </Box>
  )
}

export default WorkflowsTab
